import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Tab, Tabs } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { faxApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'
import { useAuthStore } from '../store/authStore'

interface FaxExtension {
  id: string
  tenant_id: string
  extension: string
  name: string
  description: string
  email: string
  email_subject: string
  email_body: string
  attach_format: string
  min_rate: number
  max_rate: number
  ecm: boolean
  header_info: string
  local_station_id: string
  // IMAP Settings for outbound fax
  imap_enabled: boolean
  imap_host: string
  imap_port: number
  imap_ssl: boolean
  imap_user: string
  imap_password: string
  imap_folder: string
  imap_sent_folder: string
  imap_failed_folder: string
  imap_poll_interval: number
  outbound_caller_id: string
  is_active: boolean
  created_at: string
}

interface FaxMessage {
  id: string
  fax_extension_id: string
  direction: string
  caller_id: string
  caller_id_name: string
  remote_station_id: string
  destination: string
  file_path: string
  pages: number
  transfer_rate: number
  status: string
  error_message: string
  email_sent: boolean
  started_at: string
  completed_at: string
}

const emptyForm = {
  extension: '',
  name: '',
  description: '',
  email: '',
  email_subject: 'Neues Fax empfangen von {caller_id}',
  email_body: 'Sie haben ein Fax empfangen.\n\nVon: {caller_id}\nDatum: {date}\nSeiten: {pages}',
  attach_format: 'pdf',
  min_rate: 4800,
  max_rate: 14400,
  ecm: true,
  header_info: '',
  local_station_id: '',
  // IMAP Settings
  imap_enabled: false,
  imap_host: '',
  imap_port: 993,
  imap_ssl: true,
  imap_user: '',
  imap_password: '',
  imap_folder: 'INBOX',
  imap_sent_folder: 'Sent',
  imap_failed_folder: 'Failed',
  imap_poll_interval: 60,
  outbound_caller_id: '',
  is_active: true
}

export default function Fax() {
  const confirm = useConfirm()
  const user = useAuthStore(state => state.user)
  const [faxExtensions, setFaxExtensions] = useState<FaxExtension[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingFax, setEditingFax] = useState<FaxExtension | null>(null)
  const [formData, setFormData] = useState(emptyForm)

  // Messages view
  const [selectedFax, setSelectedFax] = useState<FaxExtension | null>(null)
  const [messages, setMessages] = useState<FaxMessage[]>([])
  const [showMessagesModal, setShowMessagesModal] = useState(false)

  useEffect(() => {
    loadFaxExtensions()
  }, [])

  const loadFaxExtensions = async () => {
    try {
      const response = await faxApi.list()
      setFaxExtensions(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden der Fax-Nebenstellen')
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async (faxExt: FaxExtension) => {
    try {
      const response = await faxApi.getMessages(faxExt.id)
      setMessages(response.data.items || [])
      setSelectedFax(faxExt)
      setShowMessagesModal(true)
    } catch {
      toast.error('Fehler beim Laden der Fax-Nachrichten')
    }
  }

  const openCreateModal = () => {
    setEditingFax(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (fax: FaxExtension) => {
    setEditingFax(fax)
    setFormData({
      extension: fax.extension,
      name: fax.name,
      description: fax.description || '',
      email: fax.email,
      email_subject: fax.email_subject,
      email_body: fax.email_body,
      attach_format: fax.attach_format,
      min_rate: fax.min_rate,
      max_rate: fax.max_rate,
      ecm: fax.ecm,
      header_info: fax.header_info || '',
      local_station_id: fax.local_station_id || '',
      // IMAP Settings
      imap_enabled: fax.imap_enabled || false,
      imap_host: fax.imap_host || '',
      imap_port: fax.imap_port || 993,
      imap_ssl: fax.imap_ssl !== false,
      imap_user: fax.imap_user || '',
      imap_password: fax.imap_password || '',
      imap_folder: fax.imap_folder || 'INBOX',
      imap_sent_folder: fax.imap_sent_folder || 'Sent',
      imap_failed_folder: fax.imap_failed_folder || 'Failed',
      imap_poll_interval: fax.imap_poll_interval || 60,
      outbound_caller_id: fax.outbound_caller_id || '',
      is_active: fax.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const submitData: Record<string, unknown> = { ...formData }
      if (!submitData.header_info) delete submitData.header_info
      if (!submitData.local_station_id) delete submitData.local_station_id
      if (!submitData.description) delete submitData.description
      // Clean up empty IMAP settings
      if (!submitData.imap_host) delete submitData.imap_host
      if (!submitData.imap_user) delete submitData.imap_user
      if (!submitData.imap_password) delete submitData.imap_password
      if (!submitData.outbound_caller_id) delete submitData.outbound_caller_id

      if (editingFax) {
        await faxApi.update(editingFax.id, submitData)
        toast.success('Fax-Nebenstelle aktualisiert')
      } else {
        submitData.tenant_id = user?.tenant_id
        await faxApi.create(submitData)
        toast.success('Fax-Nebenstelle erstellt')
      }
      setShowModal(false)
      loadFaxExtensions()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (fax: FaxExtension) => {
    const confirmed = await confirm({
      title: 'Fax-Nebenstelle löschen',
      message: `Fax-Nebenstelle "${fax.name}" (${fax.extension}) wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await faxApi.delete(fax.id)
      toast.success('Fax-Nebenstelle gelöscht')
      loadFaxExtensions()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const downloadFax = async (msg: FaxMessage) => {
    try {
      const response = await faxApi.downloadFax(msg.id)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const ext = msg.file_path?.endsWith('.pdf') ? 'pdf' : 'tiff'
      link.setAttribute('download', `fax_${msg.caller_id}_${msg.started_at}.${ext}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {
      toast.error('Fehler beim Download')
    }
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('de-CH')
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4><i className="bi bi-printer me-2"></i>Fax</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Fax-Nebenstelle
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Header>
          <i className="bi bi-info-circle me-2"></i>Fax-to-Email / Email-to-Fax
        </Card.Header>
        <Card.Body>
          <p className="mb-2">
            <strong>Empfang:</strong> Eingehende Faxe werden automatisch als PDF per E-Mail zugestellt.
            Richten Sie eine Fax-Nebenstelle ein und leiten Sie eingehende Anrufe (Inbound Route) an diese Nebenstelle weiter.
          </p>
          <p className="mb-0">
            <strong>Versand:</strong> Aktivieren Sie IMAP in den Einstellungen, um Faxe per E-Mail zu versenden.
            Senden Sie eine E-Mail mit PDF-Anhang an das konfigurierte Postfach.
            Die Faxnummer wird aus dem Betreff extrahiert (z.B. "FAX: +41321234567").
          </p>
        </Card.Body>
      </Card>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Nebenstelle</th>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Format</th>
              <th>Versand</th>
              <th>Status</th>
              <th style={{ width: 180 }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-4">Laden...</td></tr>
            ) : faxExtensions.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-4 text-muted">Keine Fax-Nebenstellen</td></tr>
            ) : (
              faxExtensions.map(fax => (
                <tr key={fax.id}>
                  <td><strong>{fax.extension}</strong></td>
                  <td>{fax.name}</td>
                  <td>{fax.email}</td>
                  <td>
                    <Badge bg="secondary">{fax.attach_format.toUpperCase()}</Badge>
                  </td>
                  <td>
                    {fax.imap_enabled ? (
                      <Badge bg="info" title={`IMAP: ${fax.imap_host}`}>
                        <i className="bi bi-envelope-check"></i> IMAP
                      </Badge>
                    ) : (
                      <Badge bg="secondary">-</Badge>
                    )}
                  </td>
                  <td>
                    <Badge bg={fax.is_active ? 'success' : 'secondary'}>
                      {fax.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-info"
                      className="me-1"
                      onClick={() => loadMessages(fax)}
                      title="Nachrichten"
                    >
                      <i className="bi bi-envelope"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-1"
                      onClick={() => openEditModal(fax)}
                      title="Bearbeiten"
                    >
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDelete(fax)}
                      title="Löschen"
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingFax ? 'Fax-Nebenstelle bearbeiten' : 'Neue Fax-Nebenstelle'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Tabs defaultActiveKey="basic" className="mb-3">
              <Tab eventKey="basic" title="Grundeinstellungen">
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nebenstelle *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.extension}
                        onChange={(e) => setFormData({...formData, extension: e.target.value})}
                        placeholder="z.B. 199"
                        required
                        disabled={!!editingFax}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="z.B. Empfangsfax"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Beschreibung</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>E-Mail-Adresse *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="fax@example.com"
                    required
                  />
                  <Form.Text className="text-muted">
                    Empfangene Faxe werden an diese Adresse gesendet
                  </Form.Text>
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anhang-Format</Form.Label>
                      <Form.Select
                        value={formData.attach_format}
                        onChange={(e) => setFormData({...formData, attach_format: e.target.value})}
                      >
                        <option value="pdf">PDF</option>
                        <option value="tiff">TIFF</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Check
                        type="switch"
                        label="Aktiv"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="email" title="E-Mail-Vorlage">
                <Form.Group className="mb-3">
                  <Form.Label>Betreff</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.email_subject}
                    onChange={(e) => setFormData({...formData, email_subject: e.target.value})}
                  />
                  <Form.Text className="text-muted">
                    Variablen: {'{caller_id}'}, {'{date}'}, {'{pages}'}
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Nachricht</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={formData.email_body}
                    onChange={(e) => setFormData({...formData, email_body: e.target.value})}
                  />
                </Form.Group>
              </Tab>

              <Tab eventKey="advanced" title="Erweitert">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Min. Übertragungsrate</Form.Label>
                      <Form.Select
                        value={formData.min_rate}
                        onChange={(e) => setFormData({...formData, min_rate: parseInt(e.target.value)})}
                      >
                        <option value={2400}>2400 bps</option>
                        <option value={4800}>4800 bps</option>
                        <option value={7200}>7200 bps</option>
                        <option value={9600}>9600 bps</option>
                        <option value={14400}>14400 bps</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max. Übertragungsrate</Form.Label>
                      <Form.Select
                        value={formData.max_rate}
                        onChange={(e) => setFormData({...formData, max_rate: parseInt(e.target.value)})}
                      >
                        <option value={4800}>4800 bps</option>
                        <option value={7200}>7200 bps</option>
                        <option value={9600}>9600 bps</option>
                        <option value={14400}>14400 bps</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="ECM (Error Correction Mode)"
                    checked={formData.ecm}
                    onChange={(e) => setFormData({...formData, ecm: e.target.checked})}
                  />
                  <Form.Text className="text-muted">
                    Aktiviert Fehlerkorrektur für bessere Übertragungsqualität
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Kopfzeile (für ausgehende Faxe)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.header_info}
                    onChange={(e) => setFormData({...formData, header_info: e.target.value})}
                    placeholder="z.B. Firma GmbH"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Station ID (TSI/CSI)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.local_station_id}
                    onChange={(e) => setFormData({...formData, local_station_id: e.target.value})}
                    placeholder="z.B. +41 32 520 10 34"
                    maxLength={20}
                  />
                </Form.Group>
              </Tab>

              <Tab eventKey="imap" title="Fax-Versand (IMAP)">
                <Card className="mb-3 bg-light">
                  <Card.Body>
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      E-Mails mit PDF-Anhang an diese Mailbox werden automatisch als Fax versendet.
                      Die Faxnummer wird aus dem Betreff extrahiert (z.B. "FAX: +41321234567" oder nur die Nummer).
                    </small>
                  </Card.Body>
                </Card>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="IMAP-Fax-Versand aktivieren"
                    checked={formData.imap_enabled}
                    onChange={(e) => setFormData({...formData, imap_enabled: e.target.checked})}
                  />
                </Form.Group>
                {formData.imap_enabled && (
                  <>
                    <Row>
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>IMAP Server *</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.imap_host}
                            onChange={(e) => setFormData({...formData, imap_host: e.target.value})}
                            placeholder="imap.example.com"
                            required={formData.imap_enabled}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Port</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData.imap_port}
                            onChange={(e) => setFormData({...formData, imap_port: parseInt(e.target.value)})}
                            min={1}
                            max={65535}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="SSL/TLS verwenden"
                        checked={formData.imap_ssl}
                        onChange={(e) => setFormData({...formData, imap_ssl: e.target.checked})}
                      />
                    </Form.Group>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Benutzername *</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.imap_user}
                            onChange={(e) => setFormData({...formData, imap_user: e.target.value})}
                            placeholder="fax@example.com"
                            required={formData.imap_enabled}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Passwort *</Form.Label>
                          <Form.Control
                            type="password"
                            value={formData.imap_password}
                            onChange={(e) => setFormData({...formData, imap_password: e.target.value})}
                            placeholder="********"
                            required={formData.imap_enabled}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Posteingang</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.imap_folder}
                            onChange={(e) => setFormData({...formData, imap_folder: e.target.value})}
                            placeholder="INBOX"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Gesendet-Ordner</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.imap_sent_folder}
                            onChange={(e) => setFormData({...formData, imap_sent_folder: e.target.value})}
                            placeholder="Sent"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Fehler-Ordner</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.imap_failed_folder}
                            onChange={(e) => setFormData({...formData, imap_failed_folder: e.target.value})}
                            placeholder="Failed"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Abfrageintervall (Sekunden)</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData.imap_poll_interval}
                            onChange={(e) => setFormData({...formData, imap_poll_interval: parseInt(e.target.value)})}
                            min={10}
                            max={3600}
                          />
                          <Form.Text className="text-muted">
                            Wie oft das Postfach auf neue E-Mails geprüft wird
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Ausgehende Rufnummer</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.outbound_caller_id}
                            onChange={(e) => setFormData({...formData, outbound_caller_id: e.target.value})}
                            placeholder="z.B. +41321234567"
                          />
                          <Form.Text className="text-muted">
                            CallerID für ausgehende Faxe (leer = Nebenstelle)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" type="submit">
              {editingFax ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Messages Modal */}
      <Modal show={showMessagesModal} onHide={() => setShowMessagesModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            Fax-Nachrichten: {selectedFax?.name} ({selectedFax?.extension})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table hover size="sm">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Richtung</th>
                <th>Absender/Empfänger</th>
                <th>Seiten</th>
                <th>Status</th>
                <th>E-Mail</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {messages.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-4 text-muted">Keine Nachrichten</td></tr>
              ) : (
                messages.map(msg => (
                  <tr key={msg.id}>
                    <td>{formatDate(msg.started_at)}</td>
                    <td>
                      <Badge bg={msg.direction === 'inbound' ? 'info' : 'primary'}>
                        {msg.direction === 'inbound' ? 'Eingehend' : 'Ausgehend'}
                      </Badge>
                    </td>
                    <td>{msg.caller_id || msg.destination || '-'}</td>
                    <td>{msg.pages}</td>
                    <td>
                      <Badge bg={msg.status === 'success' ? 'success' : msg.status === 'failed' ? 'danger' : 'warning'}>
                        {msg.status === 'success' ? 'OK' : msg.status === 'failed' ? 'Fehler' : msg.status}
                      </Badge>
                    </td>
                    <td>
                      {msg.email_sent ? (
                        <Badge bg="success"><i className="bi bi-check"></i></Badge>
                      ) : (
                        <Badge bg="secondary">-</Badge>
                      )}
                    </td>
                    <td>
                      {msg.file_path && msg.status === 'success' && (
                        <Button size="sm" variant="outline-primary" onClick={() => downloadFax(msg)}>
                          <i className="bi bi-download"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMessagesModal(false)}>
            Schliessen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
